import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { endOfMonth, startOfMonth } from "date-fns";
import { memo, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import ReactModal from "react-modal";
import { Link } from "react-router-dom";

import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as LockIcon } from "assets/icons/lock.svg";
import Chip from "components/Chip";
import { useGetIncomes, useGetIncomesDetail } from "hooks/apis/user";
import format from "lib/format";

const size = 50;

interface Props {
  id: number;
  startDate?: string;
  endDate?: string;
  answerType?: number;
}

const TITLES: Record<number, string> = {
  0: "기본 답변 내역 ",
  1: "보너스 답변 내역",
  2: "경고 답변 내역",
  3: "재답변 내역",
};

const IncomePreview = ({ id, startDate, endDate, answerType }: Props) => {
  const modal = useModal();
  const rootRef = useRef(null);
  const { ref, inView } = useInView({ root: rootRef.current, threshold: 0 });
  const { data, totalItems, currentPage, hasNextPage, loadMore } =
    useGetIncomes({
      id,
      size,
      startDate,
      endDate,
      answerType,
    });
  const { data: result } = useGetIncomesDetail({
    id,
    startDate,
    endDate,
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      loadMore();
    }
  }, [inView, hasNextPage]);

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const startDateFormatted =
    result?.startDate ??
    format(startDate || startOfMonth(new Date()), "yyyy.MM.dd");

  const endDateFormatted =
    result?.endDate ?? format(endDate || endOfMonth(new Date()), "yyyy.MM.dd");

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] min-h-[720px] flex justify-center items-center size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <main className="bg-white flex flex-col size-[50rem] p-[1.875rem] rounded-md">
        <header className="flex justify-end w-full relative">
          <h2 className="absolute text-center inset-0 justify-self-center self-center font-bold text-24 leading-[1.375rem]">
            {TITLES[answerType ?? 10] ?? "급여 내역 상세"}
          </h2>
          <button className="relative z-10" type="button" onClick={closeModal}>
            <CloseIcon className="size-[2.25rem]" />
          </button>
        </header>
        <section className="flex flex-col flex-1 overflow-auto pt-[2.625rem]">
          <span className="text-16 text-secondary">
            기간 : {startDateFormatted} ~ {endDateFormatted}
          </span>
          <div className="mt-[0.625rem] flex-1 flex flex-col overflow-hidden">
            <table className="flex flex-col overflow-hidden">
              <thead className="text-16 text-white w-full">
                <tr className="flex">
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary rounded-l-md">
                    번호
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary">
                    상태
                  </th>
                  <th className="h-14 flex items-center flex-1 bg-secondary px-5 text-left">
                    제목
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary">
                    답변일
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary rounded-r-md">
                    금액
                  </th>
                </tr>
              </thead>
              <tbody
                ref={rootRef}
                className="flex-1 overflow-auto w-full text-center text-secondary font-medium text-16"
              >
                {data?.map((question, index) => (
                  <tr key={question.bNo} className="flex border-b border-gray">
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      {(
                        totalItems -
                        (index + 20 * (currentPage - 1))
                      ).toLocaleString()}
                    </td>
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      <Chip
                        color={
                          question.answers?.[0]?.status === "완료"
                            ? "blue"
                            : question.answers?.[0]?.status === "작성중"
                              ? "gray"
                              : "orange"
                        }
                      >
                        {question.answers?.[0]?.status ?? ""}
                      </Chip>
                    </td>
                    <td className="h-14 flex items-center flex-1 text-left px-5">
                      <Link
                        className="flex items-center hover:underline duration-100"
                        to={`/qna/${question.bNo}`}
                        state={{
                          userId: id,
                          size,
                          startDate,
                          endDate,
                        }}
                      >
                        {question.secret && (
                          <LockIcon className="size-6 shrink-0" />
                        )}
                        <p className="line-clamp-1 break-all">
                          {question.title}
                        </p>
                      </Link>
                    </td>
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      {format(
                        question.answers?.[0].registrationDate,
                        "yyyy/MM/dd",
                      )}
                    </td>
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      <span className="block">
                        {question.answers?.[0]?.reward && question.enabled
                          ? `+${question.answers?.[0]?.reward?.toLocaleString?.()}`
                          : 0}
                      </span>
                      {!!question.answers?.[0]?.bonus && question.enabled && (
                        <span className="text-14">
                          {`(보너스 ${question.answers?.[0]?.bonus?.toLocaleString?.()})`}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="block h-px" ref={ref}></tr>
              </tbody>
            </table>
          </div>
          {!!data?.length && (
            <div className="flex text-secondary font-medium text-center py-[1.688rem] border-t border-secondary">
              {typeof answerType === "number" ? (
                <p className="flex-1 text-left">
                  총 {totalItems.toLocaleString()}건
                </p>
              ) : [1, 2].includes(result?.gradeId ?? 0) ? (
                <p className="flex-1">
                  기본 {result?.completedCount ?? 0}건 + 경고{" "}
                  {(result?.warningCount ?? 0).toLocaleString()}건 + 재답변{" "}
                  {(result?.reanswerCount ?? 0).toLocaleString()}건 = 총{" "}
                  {result
                    ? (
                        result.completedCount +
                        result.bonusCount +
                        result.reanswerCount +
                        result.warningCount
                      ).toLocaleString()
                    : 0}
                  건
                </p>
              ) : (
                <p className="flex-1">
                  기본 {(result?.completedCount ?? 0).toLocaleString()}건 +
                  보너스 {(result?.bonusCount ?? 0).toLocaleString()}건 + 경고{" "}
                  {(result?.warningCount ?? 0).toLocaleString()}건 = 총{" "}
                  {result
                    ? (
                        result.completedCount +
                        result.bonusCount +
                        result.warningCount
                      ).toLocaleString()
                    : 0}
                  건
                </p>
              )}
              <span className="w-[6.813rem]">총 지급 액</span>
              <span className="text-primary w-[9.625rem]">
                {(+(result?.totalReward ?? 0)).toLocaleString()} 원
              </span>
            </div>
          )}
        </section>
      </main>
    </ReactModal>
  );
};

const IncomePreviewModal = NiceModal.create(memo(IncomePreview));
IncomePreviewModal.displayName = "IncomePreviewModal";

export default IncomePreviewModal;
